/**
 * 발행본 PDF 에서 **옮겨 적기** 위한 붙여넣기 파서.
 *
 * 2026년 건설공사 표준품셈은 982쪽이다. 한 칸씩 타이핑하는 건 현실적이지 않고,
 * 실제 작업은 PDF 뷰어에서 블록을 긁어 붙여넣는 것이다. 그런데 이 PDF 의 텍스트는
 * 그대로 쓸 수 없다 — 아래 두 가지를 여기서 흡수한다.
 *
 *  1. **한글 자간이 벌어져 있다.** HWP 장평 때문에 `불 도 저 (무 한 궤 도 )` 처럼
 *     글자마다 공백이 낀다. 이걸 안 붙이면 검색도 역검색도 안 걸린다.
 *  2. **열 구분이 탭이 아니라 공백**이다. 그래서 "2칸 이상 공백"을 열 경계로 본다.
 *
 * 전부 순수 함수다. 파싱은 조용히 틀리는 종류의 코드라 스펙으로 묶어뒀다.
 */

import type {
  PumsemBlock,
  PumsemListItem,
  PumsemListStyle,
} from '../model/pumsem.type';
import type { ParsedHeading } from '../model/pumsemPaste.type';

/** 장평 조각으로 볼 최대 길이 — 한글 한 글자 + 붙어 있는 괄호 하나. */
const SPREAD_PIECE_MAX = 2;
/** 이만큼 줄지어 나와야 장평으로 본다. 하나뿐이면 `및` 같은 진짜 한 글자 단어다. */
const SPREAD_RUN_MIN = 2;

/** 한글 한 글자짜리 토막(붙어 있는 괄호까지 허용) — 장평으로 벌어진 조각이다. */
const isSpreadPiece = (token: string): boolean => {
  const syllables = token.match(/[가-힣]/g) ?? [];
  return syllables.length === 1 && token.length <= SPREAD_PIECE_MAX;
};

/** 벌어진 조각에 딸려 다니는 괄호만 있는 토막. */
const isPunctOnly = (token: string): boolean => /^[()[\]]+$/.test(token);

/**
 * `불 도 저 (무 한 궤 도 )` → `불도저(무한궤도)`
 *
 * ── 한 글자 토막이 **연달아 둘 이상**일 때만 붙인다 ──────────
 * 그냥 "한글 사이 공백을 지운다" 로 하면 `유도선 설치 및 해체` 가
 * `유도선설치및해체` 가 된다 — 거긴 진짜 띄어쓰기다. HWP 장평은 **글자마다**
 * 공백을 넣으므로, 한 글자 토막이 줄지어 나오는 구간만 원래 한 단어다.
 *
 * 그래서 이 함수는 **표 셀에만** 쓴다. 제목 줄은 발행본에서도 정상 간격이라
 * 손대지 않는다.
 */
export function normalizeHangulSpacing(text: string): string {
  const tokens = text.trim().split(/ +/);
  const out: string[] = [];
  let run: string[] = [];

  const flush = () => {
    if (run.length === 0) return;
    // 조각이 하나뿐이면 진짜 한 글자 단어(`및`, `외`)다. 붙이지 않는다.
    const singles = run.filter(isSpreadPiece).length;
    if (singles >= SPREAD_RUN_MIN) out.push(run.join(''));
    else out.push(...run);
    run = [];
  };

  for (const token of tokens) {
    if (isSpreadPiece(token) || (run.length > 0 && isPunctOnly(token))) {
      run.push(token);
    } else {
      flush();
      out.push(token);
    }
  }
  flush();

  return out.join(' ').replace(/\( /g, '(').replace(/ \)/g, ')');
}

/** `1-1-2 유도선 설치 및 해체('08년 신설, '17, '21년 보완)` 를 세 조각으로. */
const HEADING = /^\s*(\d{1,3}(?:-\d{1,3}){0,3})\s+(.+?)\s*$/;

/**
 * 제목 줄 파싱.
 *
 * 개정 표기는 **`신설`/`보완` 으로 끝나는 괄호**만 떼어낸다. `굴착기(무한궤도)`
 * 처럼 이름의 일부인 괄호를 떼면 품셈명이 망가진다.
 *
 * 자간 정규화는 **하지 않는다.** 발행본 제목은 정상 간격이라, 붙이면
 * `유도선 설치 및 해체` 가 `유도선설치및해체` 가 된다.
 */
export function parseHeadingLine(line: string): ParsedHeading | null {
  const matched = HEADING.exec(line.trim());
  if (!matched) return null;

  const code = matched[1];
  let title = matched[2];
  const revisionMarks: string[] = [];

  const marks = /\(([^()]*(?:신설|보완)[^()]*)\)\s*$/.exec(title);
  if (marks) {
    title = title.slice(0, marks.index).trim();
    // 쉼표로 자르면 안 된다. `'17, '21년 보완` 은 **한 표기**고 쉼표는 연도
    // 나열용이라, 잘라 놓으면 `'17` 같은 뜻 없는 조각이 남는다. 표기의 끝은
    // 늘 `신설`/`보완` 이므로 그 뒤에서 자른다(추출 스크립트와 같은 규칙).
    for (const part of marks[1].split(/(?<=신설|보완)/)) {
      const mark = part.replace(/^\s*,\s*/, '').trim();
      if (mark) revisionMarks.push(mark);
    }
  }

  return { code, title: title.trim(), revisionMarks };
}

/**
 * `[주] ① 본 품은 … ② … ③ …` → 세 줄.
 *
 * 원문 번호(①②③)는 **떼지 않는다.** 실무자가 "[주] ②를 보라"로 인용하기 때문에
 * 번호가 곧 주소다. 줄바꿈으로 이미 나뉘어 있으면 그대로 쓴다.
 */
export function splitNotes(text: string): string[] {
  const body = text.replace(/^\s*\[?\s*주\s*\]?\s*/, '');
  const byMarker = body.split(/(?=[①-⑳])/g);
  const parts = byMarker.length > 1 ? byMarker : body.split(/\r?\n/);
  return parts.map((part) => part.trim()).filter(Boolean);
}

/**
 * 서술 문단 → 줄 배열. 원본의 `◦`·`·`·`-` 글머리는 떼고 문장만 남긴다.
 * 글머리는 화면이 그리는 것이라, 데이터에 남으면 두 번 찍힌다.
 */
export function splitParagraphs(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[◦○●•·▪\-*]\s*/, '').trim())
    .filter(Boolean);
}

/**
 * 표 블록 → 셀 격자.
 *
 * 열 경계는 **2칸 이상 공백**이다. PDF 는 탭을 쓰지 않고 자리를 공백으로 채운다.
 * 붙여넣은 표가 탭 구분(엑셀·한글에서 복사)이면 그쪽을 우선한다 — 그게 더 정확하다.
 *
 * 행마다 셀 개수가 다를 수 있다(원본의 병합·빈칸). 맞추는 건 화면의 일이고,
 * 여기서는 **본 대로** 돌려준다 — 파서가 임의로 채우면 어긋난 자리를 못 찾는다.
 */
export function parseTableBlock(text: string): string[][] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim() !== '')
    .map((line) => {
      const cells = line.includes('\t')
        ? line.split('\t')
        : line.trim().split(/ {2,}/);
      return cells.map((cell) => normalizeHangulSpacing(cell));
    });
}

/**
 * 발행본의 번호 체계. **순서가 곧 깊이**다 — `1.` 안에 `가.`, 그 안에 `1)`.
 *
 * PDF 에서 긁으면 들여쓰기가 남지 않는다(추출 텍스트는 전부 왼쪽 정렬이다).
 * 그래서 중첩을 공백이 아니라 **번호 종류의 변화**로 읽는다. 발행본이 이
 * 순서를 지키기 때문에 성립하는 규칙이고, 안 지키는 항목은 화면에서 고친다.
 */
const LIST_MARKERS: { style: PumsemListStyle; pattern: RegExp }[] = [
  { style: 'decimal', pattern: /^(\d{1,2})\.\s+(.*)$/ },
  { style: 'hangul', pattern: /^([가-힣])\.\s+(.*)$/ },
  { style: 'paren', pattern: /^(\d{1,2})\)\s+(.*)$/ },
  { style: 'paren-hangul', pattern: /^([가-힣])\)\s+(.*)$/ },
  { style: 'circled', pattern: /^([①-⑳])\s*(.*)$/ },
];

interface MarkedLine {
  rank: number;
  style: PumsemListStyle;
  marker: string;
  text: string;
}

const readMarker = (line: string): MarkedLine | null => {
  for (const [rank, entry] of LIST_MARKERS.entries()) {
    const matched = entry.pattern.exec(line);
    if (matched) {
      return {
        rank,
        style: entry.style,
        marker: matched[1],
        text: matched[2].trim(),
      };
    }
  }
  return null;
};

/**
 * 붙여넣은 본문 → 블록 배열.
 *
 * `1.`~`8.` 같은 번호목록과 그 아래 `가.`~`아.` 를 중첩까지 만들어 낸다.
 * 번호가 없는 줄은 문단이고, 목록 항목 뒤에 오면 **그 항목의 이어지는 줄**로
 * 붙인다 — PDF 는 한 항목을 여러 줄로 흘려 놓는다.
 *
 * 번호는 파서가 다시 매기지 않고 **원문 그대로** 담는다. 개정으로 번호가
 * 건너뛴 목록이 있고, 실무자가 "적용방법 7항" 으로 인용하므로 번호가 주소다.
 */
export function parseBodyText(text: string): PumsemBlock[] {
  const blocks: PumsemBlock[] = [];
  /** 열려 있는 목록들. 얕은 것부터 쌓인다. */
  const stack: { rank: number; items: PumsemListItem[] }[] = [];

  const openList = (line: MarkedLine): PumsemListItem[] => {
    const items: PumsemListItem[] = [];
    const block: PumsemBlock = { type: 'list', style: line.style, items };

    if (stack.length === 0) {
      blocks.push(block);
    } else {
      // 바로 위 목록의 마지막 항목 아래로 들어간다.
      const parent = stack[stack.length - 1].items;
      const owner = parent[parent.length - 1];
      if (!owner) {
        blocks.push(block);
      } else {
        owner.children.push(block);
      }
    }

    stack.push({ rank: line.rank, items });
    return items;
  };

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.replace(/^\s*[◦○●•·▪]\s*/, '').trim();
    if (!line) continue;

    const marked = readMarker(line);
    if (!marked) {
      // 번호 없는 줄. 목록 안이면 직전 항목의 이어지는 줄이다.
      const top = stack[stack.length - 1];
      const owner = top?.items[top.items.length - 1];
      if (owner) owner.text = `${owner.text} ${line}`.trim();
      else blocks.push({ type: 'paragraph', text: line });
      continue;
    }

    while (stack.length > 0 && stack[stack.length - 1].rank > marked.rank) {
      stack.pop();
    }

    const current = stack[stack.length - 1];
    const items =
      current && current.rank === marked.rank ? current.items : openList(marked);

    items.push({ marker: marked.marker, text: marked.text, children: [] });
  }

  return blocks;
}

/**
 * `6-2-4` → `6-2-5`. 마지막 마디만 올린다.
 *
 * 원본을 순서대로 옮길 때 코드는 거의 항상 1씩 는다. 저장하고 다음 항목으로
 * 넘어갈 때 이 값을 미리 채워 두면 타이핑이 한 번 줄고, 무엇보다 **어디까지
 * 옮겼는지**를 화면이 기억한다.
 */
export function nextItemCode(code: string): string {
  const parts = code.split('-');
  const last = Number(parts[parts.length - 1]);
  if (!Number.isFinite(last)) return code;
  parts[parts.length - 1] = String(last + 1);
  return parts.join('-');
}
